import React, { useCallback, useState } from 'react';
import { Col, Row, Space, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { convertFromRaw, convertToRaw, EditorState, Modifier, RawDraftContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import {
  CtaDataInput,
  CtaDataObject,
  NotificationTemplateFragment,
  NotificationTemplateVariable,
} from '@/graphql/generated/graphql';
import SubjectInput from './SubjectEditor';
import RichTextEditor from './RichTextEditor';
import VariableSelect from './Variables';
import CTASelect from './CTASelect';
import PreviewTemplateModal from './PreviewTemplateModal';
import TemplateSelector from './TemplateSelector';
import { If } from '@/components';

type Props = {
  content?: RawDraftContentState;
  subject?: string;
  variables?: NotificationTemplateVariable[];
  onChangeRichTemplateBody: ({
    messageTemplate,
    rawDraftContentState,
  }: {
    messageTemplate: string;
    rawDraftContentState: RawDraftContentState;
  }) => void;
  onChangeRichTemplateSubject: (data?: string) => void;
  onAddCTAData?: (data: CtaDataInput) => void;
  ctaDataList?: Array<CtaDataObject>;
  CTARenderOptions?: Record<'text' | 'link' | 'color', boolean>;
  showVariableSelect?: boolean;
  editorClassName?: string;
  showCTASelect?: boolean;
  showTemplates?: boolean;
  notificationTemplates?: NotificationTemplateFragment[];
  onSelectTemplateData?: (templateId: string) => void;
};

const EditorContainer: React.FC<Props> = ({
  content,
  subject,
  onChangeRichTemplateBody,
  onChangeRichTemplateSubject,
  onAddCTAData,
  showVariableSelect = true,
  showCTASelect = true,
  variables,
  CTARenderOptions,
  ctaDataList,
  editorClassName,
  showTemplates = false,
  notificationTemplates,
  onSelectTemplateData,
}) => {
  const [state, setEditorState] = useState<EditorState>(() =>
    content ? EditorState.createWithContent(convertFromRaw(content)) : EditorState.createEmpty()
  );
  const [bodyEditorRefFocus, setBodyEditorRefFocus] = useState(false);
  const [subjectInputRefFocus, setSubjectInputRefFocus] = useState(false);
  const [previewModal, showPreviewModal] = useState(false);
  const [htmlTemplateMessage, setHtmlTemplateMessage] = useState(() =>
    content ? draftToHtml(convertToRaw(convertFromRaw(content))) : ''
  );

  const onChangeState = useCallback(
    (editorState: EditorState) => {
      setEditorState(editorState);
      const contentState = editorState.getCurrentContent();
      const rawDraftContentState = convertToRaw(contentState);
      const messageTemplate = draftToHtml(convertToRaw(contentState));
      setHtmlTemplateMessage(messageTemplate);
      onChangeRichTemplateBody({ messageTemplate, rawDraftContentState });
    },
    [onChangeRichTemplateBody]
  );

  const onChangeSubject = useCallback(
    ({ inputText, emoji, variable }: { inputText?: string; emoji?: string; variable?: string }) => {
      if (emoji) {
        onChangeRichTemplateSubject(subject?.concat(emoji));
      } else if (variable) {
        onChangeRichTemplateSubject(subject?.concat(variable));
      } else {
        onChangeRichTemplateSubject(inputText);
      }
    },
    [onChangeRichTemplateSubject, subject]
  );

  const focusBody = useCallback(() => {
    setBodyEditorRefFocus(true);
    setSubjectInputRefFocus(false);
  }, []);

  const focusSubject = useCallback(() => {
    setBodyEditorRefFocus(false);
    setSubjectInputRefFocus(true);
  }, []);

  const onAddVariable = useCallback(
    (variable: string) => {
      if (bodyEditorRefFocus) {
        const contentState = state.getCurrentContent();
        const selection = state.getSelection();
        const newContentState = Modifier.replaceText(contentState, selection, variable);
        const nextEditorState = EditorState.push(state, newContentState, 'insert-characters');

        onChangeState(nextEditorState);
      } else if (subjectInputRefFocus) {
        onChangeSubject({ variable });
      }
    },
    [bodyEditorRefFocus, state, onChangeState, onChangeSubject, subjectInputRefFocus]
  );

  const onAddCTA = useCallback(
    (cta: CtaDataInput) => {
      const contentState = state.getCurrentContent();
      const selection = state.getSelection();
      if (cta.variable) {
        const newContentState = Modifier.replaceText(contentState, selection, cta.variable);
        const nextEditorState = EditorState.push(state, newContentState, 'insert-characters');

        onChangeState(nextEditorState);
        onAddCTAData?.(cta);
      }
    },
    [state, onAddCTAData, onChangeState]
  );

  const onSelectTemplate = useCallback(
    (id: string) => {
      onSelectTemplateData?.(id);
    },
    [onSelectTemplateData]
  );

  const togglePreviewModal = (visible: boolean) => {
    showPreviewModal(visible);
  };

  return (
    <>
      <Row>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
          <Row gutter={[32, 32]}>
            <Col xs={12} sm={12} md={12} lg={12} xl={8} xxl={8}>
              <Space>
                <If condition={showVariableSelect}>
                  <VariableSelect variables={variables} onAddVariable={onAddVariable} />
                </If>
                <If condition={showCTASelect}>
                  <CTASelect onAddCTA={onAddCTA} renderOptions={CTARenderOptions} />
                </If>
                <If condition={showTemplates}>
                  <TemplateSelector onSelectTemplate={onSelectTemplate} notificationTemplates={notificationTemplates} />
                </If>
              </Space>
            </Col>
            <Col
              xs={{ span: 4, offset: 8 }}
              sm={{ span: 4, offset: 8 }}
              md={{ span: 3, offset: 9 }}
              lg={{ span: 3, offset: 9 }}
              xl={{ span: 3, offset: 13 }}
              xxl={{ span: 2, offset: 14 }}
            >
              <Tooltip title="Preview Notification">
                <EyeOutlined
                  style={{ fontSize: '1.7em', color: 'var(--arya-primary-color)' }}
                  onClick={() => togglePreviewModal(true)}
                />
              </Tooltip>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
              <SubjectInput onFocus={focusSubject} subject={subject} onChange={onChangeSubject} />
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
              <RichTextEditor
                onChangeState={onChangeState}
                editorState={state}
                onFocus={focusBody}
                editorClassName={editorClassName}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      {previewModal ? (
        <PreviewTemplateModal
          subject={subject}
          messageTemplate={htmlTemplateMessage}
          isModalVisible={previewModal}
          closeModal={togglePreviewModal}
          ctaDataList={ctaDataList}
          variables={variables}
        />
      ) : null}
    </>
  );
};

export default EditorContainer;
